import { Button, Heading } from '@mm/components';
import { TrashIcon } from '@mm/icons';
import { EditIcon } from '@mm/icons/AntDesignIcons';
import { useAudiencesCRUDActions } from '@mm/state/actions/AudiencesCRUDActions';
import { useContactsCRUDActions } from '@mm/state/actions/ContactsCRUDActions';
import { useViewActions } from '@mm/state/actions/viewActions';
import { FilterParams, TagsFilters } from '@mm/state/model/AudiencesModel';
import { getFormattedContacts } from '@mm/state/modules/contactTableState/selectors';
import { useModalActions } from '@mm/state/modules/modalsState/actions';
import {
  selectContacts,
  selectContactsByAudienceId,
} from '@mm/state/selectors/contactsSelector';
import { selectCurrentUserIsAdmin } from '@mm/state/selectors/userSelector';
import {
  activeChatbotIdSelector,
  connectedPageSelector,
} from '@mm/state/selectors/viewSelectors';
import { colors } from '@mm/utils';
import { isEqual } from 'lodash';
import React, { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  ButtonColor,
  PrimaryButton,
} from '../../../../../common/buttons/PrimaryButton/PrimaryButton';
import ContactsTable from '../../../contacts/components/ContactsTable/ContactTable';
import { AudienceListSidebar } from './AudienceListSidebar';
import { AudienceNameEditBar } from './AudienceNameEditBar';
import { Props as ContactSlideoutMenuProps } from './ContactSlideoutMenu/ContactSlideoutMenu';
import { FilterBar } from './FilterBar/FilterBar';
import styles from './LeadsContacts.module.css';
import { LeadsContactsToolbarButtons } from './LeadsContactsToolbarButtons';
import { useAudienceViewHandlers } from './use-audience-view-handlers';
import { useFetchAudienceContacts } from './use-fetch-audience-contacts';
import { useLeadContactsFilter } from './use-lead-contacts-filter';

const PAGE_SIZE = 25;

export const LeadsContacts = () => {
  const { activeAudienceId, setAudienceId, activeAudience } =
    useLeadContactsFilter();
  const connectedPage = useSelector(connectedPageSelector);

  const { fetchPage, isFetching: isFetchingAudienceContacts } =
    useFetchAudienceContacts(activeAudienceId, {
      pageSize: PAGE_SIZE,
      skipBusy: true,
      pauseRefreshingWhenEditingAudience: true,
    });

  const modalActions = useModalActions();
  const { showSlideoutMenu } = useViewActions();

  const contactsCRUDActions = useContactsCRUDActions();
  const audiencesCRUDActions = useAudiencesCRUDActions();
  const activeChatbotId = useSelector(activeChatbotIdSelector);

  useEffect(() => {
    if (!activeAudienceId) {
      getAllContacts(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [connectedPage, activeAudienceId]);

  const [contactFilters, setContactFilters] = useState<{
    filterParams?: FilterParams;
    tagsFilters?: TagsFilters;
  }>({});
  const audienceSavePending = Boolean(
    contactFilters.tagsFilters || contactFilters.filterParams,
  );
  const getContactsFromAudience =
    activeAudienceId && activeAudience && !audienceSavePending;

  const onFiltersUpdated = (
    filterParams: FilterParams | undefined,
    tagsFilters: TagsFilters | undefined,
  ) => {
    const needsUpdate = !(
      isEqual(filterParams, contactFilters.filterParams) &&
      isEqual(tagsFilters, contactFilters.tagsFilters)
    );
    if (needsUpdate) {
      setContactFilters({
        filterParams,
        tagsFilters,
      });
    }
  };
  const onSaveAudience = async () => {
    if (activeAudience) {
      await audiencesCRUDActions.update(
        {
          ...activeAudience,
          filterParams:
            contactFilters.filterParams || activeAudience.filterParams,
          tagsFilters: contactFilters.tagsFilters || activeAudience.tagsFilters,
        },
        activeChatbotId,
      );
      setContactFilters({});
    }
  };
  const onSaveAudienceAs = () => {
    modalActions.openNewAudienceNamePromptModal({
      onConfirmHandler: async name => {
        const newAudience = await audiencesCRUDActions.create(
          {
            name,
            filterParams: contactFilters.filterParams || {},
            tagsFilters: contactFilters.tagsFilters || {},
          },
          activeChatbotId,
        );
        setContactFilters({});
        navigateToAudience(newAudience?.id);
      },
    });
  };
  const getAllContacts = async (pageNumber?: number) => {
    if (!connectedPage) {
      return;
    }
    setRetrievingContacts(true);
    if (getContactsFromAudience) {
      await fetchPage(PAGE_SIZE, pageNumber, contactsSearchValue);
    } else {
      await contactsCRUDActions.retrieveAll(
        connectedPage.id,
        pageNumber,
        pagination?.perPage,
        contactsSearchValue,
        { skipBusy: true, audience: contactFilters },
      );
    }
    setRetrievingContacts(false);
  };
  useEffect(() => {
    getAllContacts(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contactFilters.filterParams, contactFilters.tagsFilters]);

  const onChangePagination = (pageNumber: number) => {
    getAllContacts(pageNumber);
  };

  const deleteSelectedContact = async (
    contactId: number,
    eraseRemoteMessages: boolean,
  ) => {
    if (connectedPage) {
      await contactsCRUDActions.deleteSelectedContact(
        connectedPage.id,
        contactId,
        pagination?.page,
        pagination?.perPage,
        undefined,
        eraseRemoteMessages,
      );

      await Promise.all([
        getAllContacts(pagination?.page),
        audiencesCRUDActions.retrieveAll(activeChatbotId),
      ]);
    }
  };

  const getSelectedContact = (contactId: number) =>
    openContactSlideoutMenu({
      contactId,
      deleteSelectedContact,
      onContactSelect: getSelectedContact, // user might select associated contacts within contact
      onContactUpdateSuccess: () => getAllContacts(pagination?.page),
    });

  const openContactSlideoutMenu = useCallback(
    (props: ContactSlideoutMenuProps) => {
      showSlideoutMenu('contact', props);
    },
    [showSlideoutMenu],
  );

  const onSearchContacts = () => {
    getAllContacts(1);
  };

  const allContacts = useSelector(selectContacts);
  const contactsByAudienceId = useSelector(state =>
    selectContactsByAudienceId(state, activeAudienceId),
  );
  const isSysAdmin = useSelector(selectCurrentUserIsAdmin);

  const contacts = getContactsFromAudience
    ? contactsByAudienceId?.contacts
    : allContacts.contacts;
  const pagination = getContactsFromAudience
    ? contactsByAudienceId?.pagination
    : allContacts.pagination;

  const isExportDisabled = !contacts?.length;
  const [contactsSearchEnabled, setContactsSearchEnabled] = useState(false);
  const [contactsSearchValue, setContactsSearchValue] = useState('');
  const [retrievingContacts, setRetrievingContacts] = useState(false);

  const audienceName = activeAudience?.name ?? '';
  const { onDelete, onAllowContactsDeleteToggle } = useAudienceViewHandlers();

  const [editingAudienceName, setEditingAudienceName] = useState(false);
  useEffect(() => setEditingAudienceName(false), [activeAudienceId]);

  const onEditClick = () => {
    if (activeAudienceId) {
      setEditingAudienceName(true);
    }
  };
  const onDeleteClick = () => {
    if (activeAudienceId) {
      onDelete({
        audienceId: activeAudienceId,
        audienceName,
        onSuccess: () => setAudienceId(undefined),
      });
    }
  };

  const navigateToAudience = (audienceId: number | undefined) => {
    setAudienceId(audienceId);
  };

  return (
    <section className={styles.container}>
      <AudienceListSidebar
        className={styles.audiencesSidebar}
        navigateToAudience={navigateToAudience}
        selectedAudienceId={activeAudienceId}
      />
      <div className={styles.contactsPane}>
        {editingAudienceName && activeAudience ? (
          <AudienceNameEditBar
            className={styles.mainToolbar}
            activeAudience={activeAudience}
            onEndEditing={() => setEditingAudienceName(false)}
          />
        ) : (
          <div className={styles.mainToolbar}>
            <div className={styles.mainToolbarLeft}>
              <Heading size={3}>
                {activeAudience ? audienceName : 'All Contacts'}
              </Heading>
              {!activeAudience && audienceSavePending && (
                <Button
                  color="blue"
                  onClick={onSaveAudienceAs}
                  disabled={contactsSearchEnabled}
                >
                  Save as an Audience
                </Button>
              )}
              {activeAudience && (
                <>
                  <Button
                    color="transparent-no-shadow"
                    leftIcon={<EditIcon color={colors.accentBlue} size={18} />}
                    onClick={onEditClick}
                    disabled={!activeAudienceId}
                  />
                  {audienceSavePending && (
                    <>
                      <Button
                        color="blue"
                        onClick={onSaveAudience}
                        disabled={contactsSearchEnabled}
                      >
                        Save
                      </Button>
                      <Button
                        color="secondary"
                        onClick={onSaveAudienceAs}
                        disabled={contactsSearchEnabled}
                      >
                        Save as
                      </Button>
                    </>
                  )}

                  <Button
                    color="inverted-red"
                    leftIcon={<TrashIcon color={colors.red} />}
                    onClick={onDeleteClick}
                    disabled={!activeAudienceId}
                  />
                  {activeAudienceId && isSysAdmin && (
                    <PrimaryButton
                      onClick={() =>
                        onAllowContactsDeleteToggle(
                          activeAudienceId,
                          !activeAudience.deleteContacts,
                        )
                      }
                      color={
                        activeAudience.deleteContacts
                          ? ButtonColor.red
                          : ButtonColor.secondaryBlue
                      }
                    >
                      {activeAudience.deleteContacts ? 'Disable' : 'Enable'}
                      Contacts Deletion
                    </PrimaryButton>
                  )}
                </>
              )}
            </div>

            <LeadsContactsToolbarButtons
              activeAudienceId={activeAudienceId}
              audienceName={audienceName}
              contactsSearchEnabled={contactsSearchEnabled}
              setContactsSearchEnabled={setContactsSearchEnabled}
              contactsSearchValue={contactsSearchValue}
              setContactsSearchValue={setContactsSearchValue}
              isExportDisabled={isExportDisabled}
              onSearchContacts={onSearchContacts}
            />
          </div>
        )}

        <FilterBar
          activeAudience={activeAudience}
          onFiltersUpdated={onFiltersUpdated}
          totalFound={pagination?.total}
        />
        <ContactsTable
          css={{
            wrapper: styles.contactsTableWrapper,
            container: styles.contactsTableContainer,
            rowsContainer: styles.contactsTableRowsContainer,
          }}
          hideTotal
          onChangePagination={onChangePagination}
          getSelectedContact={getSelectedContact}
          contacts={getFormattedContacts(contacts ?? [])}
          deleteSelectedContact={deleteSelectedContact}
          pagination={pagination}
          searchActive={retrievingContacts || isFetchingAudienceContacts}
          disableSorting={!!activeAudienceId}
        />
      </div>
    </section>
  );
};
